import { HardwareService } from '../../../HardwareService';
import type { BarcodeData } from '../../types';
import type { DecoderProbe, Pdf417Decoder } from './types';
import { latin1Bytes, latin1Text } from './types';

/**
 * The slice of `zxing-wasm/reader` used here, typed locally.
 *
 * The library's own signatures come through `type-fest`, which this package's `moduleResolution:
 * node` cannot resolve, and `prepareZXingModule` is an overload set keyed on `fireImmediately`.
 * Restating the two calls keeps the coupling to an upstream shape explicit and small.
 */
type ZXingReadResult = { isValid: boolean; bytes?: Uint8Array; text?: string };

type ZXingReader = {
  prepareZXingModule(options: { overrides: { locateFile: (path: string, prefix: string) => string } }): void;
  readBarcodesFromImageData(image: ImageData, options: Record<string, unknown>): Promise<ZXingReadResult[]>;
};

/**
 * Dynamic so the reader's glue is fetched on the first decode rather than shipped to every bundle
 * that touches this package, and guarded so a chunk that never arrives — a dropped fetch on a
 * dispensary connection — makes this one rung unavailable instead of throwing out of the caller.
 *
 * Only this import is guarded; the retry lives in the warm-up memo below, which already clears
 * itself on failure. Nothing caches the absence of a reader.
 */
const loadReader = async (): Promise<ZXingReader | null> => {
  try {
    return (await import('zxing-wasm/reader')) as unknown as ZXingReader;
  } catch (error) {
    HardwareService.logger.d('zxing-wasm reader module could not be imported', { error });
    return null;
  }
};

/** The exact URL the app supplied. Must be set, or the library reaches jsDelivr. */
let wasmUrl: string | null = null;

/**
 * Resolves the app-supplied URL and requires it to be same-origin.
 *
 * `zxing-wasm` defaults to fetching its binary from jsDelivr, which works on every developer machine
 * and then silently never decodes on a dispensary network that blocks third-party origins, where it
 * reaches the operator as "this card is unreadable". So a cross-origin URL is refused outright.
 *
 * This is an origin check and nothing more. It does not establish that the bytes are the reader
 * wasm, that they are intact, or that the app rather than something else put them there — a
 * same-origin URL is trusted as given. The app is responsible for pointing this at its own copy of
 * the binary; integrity, if it is ever wanted, needs a real mechanism, not a URL pattern.
 *
 * Returns the absolute href, which is unambiguous whichever document embeds the scanner, or null.
 */
export const resolveSameOriginWasmUrl = (url: string): string | null => {
  const base = typeof location === 'undefined' ? null : location.href;
  if (!base) {
    return null;
  }

  try {
    const resolved = new URL(url, base);
    return resolved.origin === new URL(base).origin ? resolved.href : null;
  } catch {
    return null;
  }
};

/** Accepts a same-origin URL. Returns false, and stays unconfigured, for anything else. */
export const setPdf417WasmUrl = (url: string): boolean => {
  const resolved = resolveSameOriginWasmUrl(url);
  if (!resolved) {
    HardwareService.logger.e('refused a PDF417 decoder wasm URL that is not same-origin', { url });
    return false;
  }

  wasmUrl = resolved;
  return true;
};

let prepared: Promise<ZXingReader> | null = null;

/**
 * How long a cold start may hang before this rung gives up. The fetch has no timeout of its own, so
 * without this the caller could never reach `BarcodeDetector` and every read would spend its whole
 * budget on the same pending promise.
 */
export const WASM_WARMUP_TIMEOUT_MS = 15_000;

/** Loads the reader, points it at the app's copy, and instantiates it by decoding a 1x1 frame. */
const instantiate = async (url: string): Promise<ZXingReader> => {
  const reader = await loadReader();
  if (!reader) {
    throw new Error('PDF417 decoder module is unavailable');
  }

  // `fireImmediately` would hand back the module directly, but its overload is typed through
  // `type-fest`, which does not resolve here — so configure, then instantiate with a blank frame.
  const locateFile = (path: string, prefix: string) => (path.endsWith('.wasm') ? url : prefix + path);
  reader.prepareZXingModule({ overrides: { locateFile } });

  // The library duck-types ImageData; the DOM type additionally requires colorSpace, unread here.
  const blank = { data: new Uint8ClampedArray(4), width: 1, height: 1 } as unknown as ImageData;
  await reader.readBarcodesFromImageData(blank, { formats: ['PDF417'] });

  return reader;
};

/** Fetches and instantiates the wasm. Idempotent; call it before the operator needs a decode. */
export const warmPdf417Decoder = (): Promise<ZXingReader> => {
  if (!wasmUrl) {
    // Fails closed rather than reaching jsDelivr: a CDN fallback works on every developer machine
    // and then silently never decodes on a locked-down dispensary network.
    HardwareService.logger.d('PDF417 wasm URL was never set; refusing the CDN fallback');
    return Promise.reject(new Error('PDF417 decoder wasm URL is not configured'));
  }

  if (prepared) {
    return prepared;
  }

  let timer: ReturnType<typeof setTimeout>;
  const stalled = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new Error('PDF417 decoder wasm did not load in time')), WASM_WARMUP_TIMEOUT_MS);
  });

  // `Promise.race` subscribes to the loser as well, so a fetch this warm-up gave up on cannot
  // surface as an unhandled rejection when it finally settles.
  const attempt: Promise<ZXingReader> = Promise.race([instantiate(wasmUrl), stalled]).then(
    (reader) => {
      clearTimeout(timer);
      return reader;
    },
    (error: unknown) => {
      clearTimeout(timer);
      // A stall is treated like a fast failure, so the next read retries. Only this attempt's own
      // memo may be cleared: a warm-up abandoned by `resetPdf417WasmState` can settle after a fresh
      // one has started, and clearing then would wipe the attempt that is actually loading.
      if (prepared === attempt) {
        HardwareService.logger.d('zxing-wasm warm-up failed', { error, timeoutMs: WASM_WARMUP_TIMEOUT_MS });
        prepared = null;
      }
      throw error;
    },
  );

  // A caller that ignores the returned promise must not turn a blocked fetch into an unhandled rejection.
  void attempt.catch(() => undefined);

  prepared = attempt;
  return attempt;
};

/** This module's state is process-wide; exists so tests and callers can start clean. */
export const resetPdf417WasmState = (): void => {
  prepared = null;
  wasmUrl = null;
};

/**
 * Points the decoder at the app's copy and warms it, so no decode pays the cold start. Returns
 * whether the URL was accepted, so an app that mis-wires it fails at startup rather than at the counter.
 */
export const configurePdf417Decoder = (url: string): boolean => {
  if (!setPdf417WasmUrl(url)) {
    return false;
  }

  void warmPdf417Decoder().catch(() => undefined);
  return true;
};

/** ZXing-C++ in WebAssembly. iOS Safari has no Shape Detection API, so on iPhones it is the only rung. */
export const zxingWasmProbe: DecoderProbe = {
  name: 'zxing-wasm',

  async create(): Promise<Pdf417Decoder | null> {
    let reader: ZXingReader;
    try {
      reader = await warmPdf417Decoder();
    } catch {
      return null;
    }

    return {
      name: zxingWasmProbe.name,
      async decode(frame: ImageData): Promise<BarcodeData | null> {
        try {
          const [found] = await reader.readBarcodesFromImageData(frame, {
            formats: ['PDF417'],
            // A card held in a hand is never axis-aligned and never evenly lit.
            tryHarder: true,
            tryRotate: true,
            // An AAMVA symbol is always dark-on-light, so inverting every frame is wasted work.
            tryInvert: false,
            tryDownscale: true,
            maxNumberOfSymbols: 1,
            // Plain, not the HRI default: HRI would rewrite the control characters AAMVAParser splits on.
            textMode: 'Plain',
          });
          if (!found?.isValid) {
            return null;
          }
          // Prefer raw bytes: the payload as encoded is what the server's parser expects.
          if (found.bytes?.length) {
            return { bytes: found.bytes, text: latin1Text(found.bytes) };
          }
          return found.text ? { bytes: latin1Bytes(found.text), text: found.text } : null;
        } catch {
          return null;
        }
      },
    };
  },
};
